// ui/BiddingControls.jsx: DOM bidding control layer (M2.c-3).
//
// Renders the 3 (r1) or 3-7 (r2) DOM buttons that drive the human's bid
// decision. Real <button> elements, keyboard, focus ring, and screen
// readers work out of the box. Canvas owns visuals; DOM owns interaction.
//
// Going-alone is a two-step decision (click → confirmation modal). The
// modal is a <dialog> element with focus trap and the stakes-complete
// text per plan v2 DM2-6.

import { useState, useEffect, useRef } from 'preact/hooks';
import { buildButtons, dealerContextLine, displayDealerContextLine, GO_ALONE_CONFIRMATION_TEXT } from './bidding-logic.js';
import { stuckDealerCaption } from './stuck-dealer-caption.js';
import { computeBidGrid } from '../canvas/bid-grid.js';

/**
 * @param {{
 *   state: object,
 *   seat: number,
 *   layout: import('../canvas/layout.js').TableLayout,
 *   onBid: (bid:{action:string,suit?:string}) => void,
 *   seatLabels?: Record<number, string>,
 *   disabledReason?: string|null,
 * }} props
 */
export function BiddingControls({ state, seat, layout, onBid, seatLabels, disabledReason = null }) {
  const buttons = buildButtons(state, seat);
  // T11, one line of dealer context above the buttons. The stuck-dealer
  // caption and its condition live in stuck-dealer-caption.js.
  const dealerLine = stuckDealerCaption(/** @type {any} */ (state), seat)
    ?? dealerContextLine(/** @type {any} */ (state), seatLabels, seat);
  /** @type {[any, (b:any)=>void]} */
  const [pendingGoAlone, setPendingGoAlone] = useState(/** @type {any} */ (null));
  /** @type {{current: HTMLDialogElement|null}} */
  const dialogRef = useRef(null);

  // Open/close the native dialog when pendingGoAlone changes.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (pendingGoAlone) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');  // jsdom / older fallback
      // Move focus to the Confirm button for fast keyboard activation.
      const confirm = d.querySelector('button[data-action="confirm-alone"]');
      if (confirm) /** @type {HTMLElement} */ (confirm).focus();
    } else {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [pendingGoAlone]);

  if (buttons.length === 0) return null;

  const handleClick = (btn) => {
    if (disabledReason) return;
    if (btn.role === 'go_alone') {
      setPendingGoAlone(btn);
      return;
    }
    onBid(btn.bid);
  };

  const confirmAlone = () => {
    if (pendingGoAlone && !disabledReason) onBid(pendingGoAlone.bid);
    setPendingGoAlone(null);
  };

  const cancelAlone = () => setPendingGoAlone(null);

  // Y1/Y2: the grid picks a free band that holds this round's rows at
  // touch size (40px phones, 36px desktop) instead of shrinking them.
  const grid = computeBidGrid(layout, buttons.length);
  const { zone, columns, rows, gap, lineHeight, buttonHeight } = grid;

  return (
    <>
      <div
        class="bidding-controls"
        role="toolbar"
        aria-label="Bidding controls"
        data-cramped={grid.cramped ? 'true' : undefined}
        style={{
          position: 'absolute',
          left: zone.x,
          top:  zone.y,
          width:  zone.w,
          height: zone.h,
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gridTemplateRows: `${lineHeight}px repeat(${rows}, ${buttonHeight}px)`,
          gap,
          alignContent: 'start',
          padding: 2,
          boxSizing: 'border-box',
        }}
      >
        {(dealerLine || disabledReason) && (
          <p
            class="bidding-dealer-context"
            data-testid={disabledReason ? 'bidding-disabled-reason' : 'bidding-dealer-context'}
            role={disabledReason ? 'status' : undefined}
            style={{
              gridColumn: `1 / span ${columns}`,
              margin: 0,
              textAlign: 'center',
              fontSize: grid.dealerFontSize,
              lineHeight: `${lineHeight}px`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 600,
              color: '#9A8AB8',
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            }}
          >
            {disabledReason || displayDealerContextLine(dealerLine, zone.w, layout.portrait)}
          </p>
        )}
        {buttons.map((btn) => (
          <button
            key={btn.key}
            type="button"
            data-bid-role={btn.role}
            data-bid-suit={btn.suit || ''}
            aria-label={btn.ariaLabel}
            disabled={!!disabledReason}
            aria-disabled={!!disabledReason}
            onClick={() => handleClick(btn)}
            style={{
              background: btn.color,
              color: btn.textColor,
              border: 'none',
              borderRadius: 6,
              padding: 0,
              fontFamily: 'inherit',
              fontWeight: 600,
              fontSize: grid.fontSize,
              minHeight: 0,
              minWidth: 0,
              height: buttonHeight,
              whiteSpace: 'nowrap',
              cursor: disabledReason ? 'not-allowed' : 'pointer',
              opacity: disabledReason ? 0.45 : 1,
              boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Going-alone confirmation modal */}
      <dialog
        ref={dialogRef}
        data-bid-modal="go-alone"
        aria-modal="true"
        aria-labelledby="alone-title"
        style={{
          padding: 0,
          border: 'none',
          borderRadius: 8,
          background: '#1F1828',
          color: '#F5F5F0',
          maxWidth: 360,
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}
        onClose={cancelAlone}
        onCancel={cancelAlone}
      >
        <div style={{ padding: '20px 22px' }}>
          <h2
            id="alone-title"
            style={{
              margin: '0 0 12px 0',
              fontSize: 16,
              color: '#F0C040',
              fontWeight: 700,
            }}
          >
            Go alone?
          </h2>
          <p style={{ margin: '0 0 18px 0', fontSize: 14, lineHeight: 1.5 }}>
            {GO_ALONE_CONFIRMATION_TEXT}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              data-action="cancel-alone"
              onClick={cancelAlone}
              style={{
                background: '#5A5A5A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontFamily: 'inherit',
                fontSize: 14,
                minHeight: 44,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-action="confirm-alone"
              onClick={confirmAlone}
              style={{
                background: '#F0A030',
                color: '#1A1422',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontFamily: 'inherit',
                fontWeight: 700,
                fontSize: 14,
                minHeight: 44,
                cursor: 'pointer',
              }}
            >
              Go alone
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
